import { Moment } from 'moment';
import type { Moment as TimeZoneMoment } from 'moment-timezone';
import { User } from './users.types';
import { Session } from './session.types';

export interface Deal {
  id: string;
  published?: DealVersion;
  working?: DealVersion;
  public?: DealVersion;
}

export enum DealVersionSelector {
  published = 'published',
  working = 'working',
  public = 'public'
}

export enum DealUserRole {
  bookrunner = 'Bookrunner',
  syndicate = 'Syndicate',
  issuer = 'Issuer',
  financialAdvisor = 'Financial Advisor',
  counsel = 'Bond Counsel',
  obligor = 'Obligor',
  none = 'None'
}

export interface DealInformation {
  dealId: string;
  deal: DealVersion;
  selectedVersion: DealVersionSelector;
  availableVersions: DealVersionSelector[];
  userRole: DealUserRole;
  loading: boolean;
  loaded: boolean;
  hasWorkingVersion: boolean;
  error?: string;
  persona: Session.Persona;
}

export interface BasicDealVersion {
  id: string;
  dealType: DealType;
  size: number;
  description: string;
  isBankQualified: boolean;
  seniorManager: Departement;
  issuer: Departement;
  obligor: Departement;
  financialAdvisors: Departement[];
  bondCounsels: Departement[];
  saleDate: Moment | TimeZoneMoment;
  saleDateTimeZone: string;
  expectedPricingDate: Moment;
  isWeekOf: boolean;
  isDayToDay: boolean;
  isTBD: boolean;
  status: DealStatus;
  orderPeriod: OrderPeriod;
  repricingOrderPeriod: OrderPeriod;
  syndicate: SyndicateMember[];
  pricingCommitted: boolean;
  assignedUsers?: string[];
  compositeDeal?: boolean;
  soleMgd?: boolean;
  issueVersion?: string;
  roadshowURL: string;
  posURL: string;
  parityPlus?: boolean;
}

export interface BasicDeal extends BasicDealVersion {
  series: BasicSeries[];
  maturities: BasicMaturity[];
}

export interface DealVersion extends BasicDealVersion {
  posURL: string;
  roadshowURL: string;
  documents: Documents[];
  categories: OrderCategory[];
  gamedaySettings: GamedaySettings;
  series: Series[];
  maturities: Maturity[];
  metrics: Metrics;
  users?: User[];
  bidSubmitted?: boolean;
  bidStatus?: string;
  // temporarily put it here until we fetch issue/series into the expanded bid
  notes?: string;
}

export interface BasicSeries {
  globalIssueId: string;
  id: string;
  size: number;
  description: string;
  code: string;
  state: string;
  isAMT: boolean;
  isTaxable: boolean;
  isQIB: boolean;
  isExtraordinaryCall: boolean;
  ratings: Rating[];
  underlyingRatings: Rating[];
  insuredRatings?: null | Rating[];
  enhancedRatings?: null | Rating[];
  insurer: string;
  callFeatures: CallFeature[];
  sourceOfRepayment: SourceOfRepayment;
  subSectors: string[];
  refunding: Purpose;
  remarketing: Purpose;
  advancedRefunding: Purpose; // from advRefunding
  newMoney: Purpose;
  interestType: InterestType;
  corpCusip: boolean;
  freeTextHdr: string;
  firstCouponDate: Moment;
  datedDate: Moment;

  securityTypes: SecurityType[];
  esgCertification: ESGCertification;
  isBankQualified: boolean;
  conduitIssuerDepartment: Departement;
  obligor: Departement;
  taxableStateInd: boolean;
  altMinTaxStateInd: boolean;
  forwardInd: boolean;
  structure: string;
}

export interface Series extends BasicSeries {
  couponFrequency: CouponFrequency;
  deliveryDate: Moment;
  settleDate: Moment;
  awardDate: Moment;
  initialTradeDate: Moment;
}

export interface BasicMaturity {
  id: string;
  seriesId: string;
  maturityDate: Moment;
  size: number;
  numberBonds: number;
  presentValue: number;
  securityType: SecurityType;
  callFeature: CallFeature;
  chartDate: Moment;
  putMandOpt: PutType;
  putDate: Moment;
  price: number;
  coupon: number;
  freeText: string;
  yield: number;
  insurer: string;
  sinkingFund: SinkingFund[];
  taxableTreasurySpread?: number;
  taxableTsyMat: string;
  pacBond?: PacBondDesignation;
  yieldSpreadBump: number;
  yieldSpreadBumpValid: boolean;
}

export interface Maturity extends BasicMaturity {
  isOrderPeriodTerminated: boolean; // from orderPeriodTerminated
  couponFrequency: CouponFrequency; // from couponFreq
  dayCount: string;
  priceToMaturity: number;
  yieldToMaturity: number;
  price: number;
  isSealedBid: boolean; // from sealedBidInd
  isNotReoffered: boolean; // from notReoffered
  isSoldNotAvailable: boolean; // from soldNotAvailable
  noRetail: boolean;
  cusip: string;
  ratings: Rating[];
  sinkingFund: SinkingFund[];
  taxableTsySpread: number;
  taxableTsyCoupon: number;
  taxableTsyYield: number;
  deletedInd?: boolean;
}
export interface ChartMaturity extends BasicMaturity {
  dealType: DealType;
  filteredOut: boolean;
}

export enum DealType {
  competitive = 'Competitive', // from Comp
  negotiated = 'Negotiated', // from Neg
  privatePlacement = 'PrivatePlacement' // from PP
}

export enum DealStatus {
  active = 'Active', // from act
  cancelled = 'Cancelled', // from can
  postponed = 'Postponed', // from ppd
  notBought = 'Not Bought', // from notbght
  bought = 'Bought' // from bght
}

export enum PreciseDealPricingStatus {
  Live = 'Live',
  Expected = 'Expected',
  Priced = 'Priced',
  Sold = 'Sold',
  Closed = 'Closed',
  Pending = 'Pending'
}

export type BasicDealPricingStatus = Exclude<
  PreciseDealPricingStatus,
  PreciseDealPricingStatus.Pending
>;

export enum OrderPeriodType {
  institutional = 'Institutional', // from I
  retail = 'Retail', // from R
  all = 'All' // from B
}

export enum SyndicateRole {
  seniorManager = 'Senior Manager', // from SM
  coSenior = 'Co-Senior Manager', // from CS
  coManager = 'Co-Manager', // from CM
  sellingGroup = 'Selling Group', // from SG
  member = 'Member', // from MEM
  nonManager = 'Non-Manager' // from NM
}

export enum CouponFrequency {
  annual = 'Annual', // from A
  monthly = 'Monthly', // from M
  quarterly = 'Quarterly', // from Q
  semiAnnual = 'Semi-Annual' // from S
}

export enum RatingStatus {
  underReview = 'Under Review', // from UR
  notAppliedFor = 'Not Applied For', // from NAF
  notAvailable = 'Not Available', // from NA
  rated = 'Rated', // from R
  expected = 'Expected Rating' // from EXP
}

export enum RatingOutlook {
  steady = 'Steady', // from STD
  negative = 'Negative', // from NEG
  positive = 'Positive', // from POS
  none = ''
}

export const enum OutlookLabel {
  Pos = 'Pos',
  Neg = 'Neg',
  STB = 'STB',
  NONE = '-'
}

export enum SourceOfRepayment {
  generalObligation = 'General Obligation', // from GO
  unlimitedTaxGo = 'Unlimited Tax GO', // from UTG
  limitedTaxGo = 'Limited Tax GO', // from LTG
  revenue = 'Revenue', // from R
  certificateOfParticipation = 'COP/Lease', // from COP
  appropriation = 'Appropriation', // from APP
  assetBackedTobacco = 'Asset Backed/Tobacco', // from AST
  corporateBacked = 'Corporate Backed', // from COR
  mortgageHousing = 'Mortgage/Housing', // from MTG
  taxAllocIncrement = 'Tax Alloc/Increment', // from TAX
  governmentGrantBacked = 'Government/Grant Backed', // from GOV
  personalIncomeTax = 'Personal Income Tax', // from ITX
  salesExciseTax = 'Sales/Excise Tax', // from STX
  fuelVehicleTax = 'Fuel/Vehicle Tax', // from FTX
  hotelTax = 'Hotel Tax', // from HTX
  cigaretteTax = 'Cigarette Tax', // from CTX
  miscellaneousTax = 'Miscellaneous Tax', // from MTX
  specialAssessment = 'Special Assessment', // from SPA
  carRentalTax = 'Car Rental Tax', // from RTX
  salesServiceLoanAgreement = 'Sales/Service/Loan Agreement', // from SAL
  lotteryGamingRevenue = 'Lottery/Gaming Revenue', // from LOT
  doubleBarreled = 'Double-Barreled' // from DBB
}

export enum IssueType {
  newMoney = 'New Money',
  refunding = 'Refunding',
  advancedRefunding = 'Advanced Refunding',
  remarketing = 'Remarketing'
}

export enum IsBankQualified {
  No,
  Yes
}

export enum IsExtraordinaryCall {
  No,
  Yes
}

export enum IsQIB {
  No,
  Yes
}

export enum IsSoleMgd {
  No,
  Yes
}

export enum SecurityType {
  bond = 'Bond',
  note = 'Note',
  put = 'Put',
  option = 'Option',
  zero = 'Zero'
}

export enum SecurityTypeFormated {
  Bond = 'Bonds',
  Note = 'Notes',
  Put = 'PUTs',
  Zero = 'Zeros'
}
export enum CallType {
  callable = 'Callable', // from c
  callableAnytime = 'Callable Anytime', // from ca
  makeWholeCall = 'Make Whole Call', // from mwc
  notCallable = 'Not Callable', // from nc
  pyramid = 'Pyramid' // from py
}

export enum InterestType {
  floatingRate = 'Floating Rate', // from FR
  variableRate = 'Variable Rate', // from VR
  fixedRate = 'Fixed Rate' // FXR
}

export enum PutType {
  mandatory = 'Mandatory',
  optional = 'Optional'
}

export enum PriceType {
  par = 'Par',
  premium = 'Premium',
  discount = 'Discount'
}

export enum DealIconType {
  none = 'none',
  loading = 'loading',
  followed = 'followed',
  groupFollowed = 'groupFollowed',
  assigned = 'assigned',
  otherAssigned = 'otherAssigned',
  originator = 'originator',
  bookrunner = 'bookrunner',
  syndicate = 'syndicate'
}

export enum DealFollowType {
  GroupFollowed = 'groupFollow',
  Followed = 'follow',
  NotFollowed = 'notFollow',
  Assigned = 'assigned',
  OtherAssigned = 'otherAssigned'
}
export enum OrderPeriodOrderTypeIndicator {
  groupNet = 'GN', //Group Net
  groupConcession = 'GC', //Group Concession
  netDesignated = 'ND', //Net Designated
  concessionDesignated = 'CD', //Concession Designated
  member = 'MEM', //Member
  preSale = 'PS', //Pre-Sale
  preSaleGroupNet = 'PSGN', //Pre-Sale Group Net
  preSaleGroupConcession = 'PSGC', //Pre-Sale Group Concession
  preSaleNetDesignated = 'PSND', //Pre-Sale Net Designated
  preSaleConcessionDesignated = 'PSCD', //Pre-Sale Concession Designated
  retail = 'RTL' //Retail
}

export interface Departement {
  id: number;
  name: string;
  orionId?: number;
  shortName?: string;
  brsIssuerId?: string;
}

export interface SyndicateMember {
  id: number;
  departmentName: string;
  role: SyndicateRole;
  orionId?: number;
  shortName?: string;
  bracket?: number;
  priority?: number;
  deptId?: number;
}

export interface OrderPeriod {
  beginDate: Moment;
  endDate: Moment;
  type: OrderPeriodType;
  orderTypes: OrderPeriodOrderType[];
}

export interface OrderPeriodOrderType {
  orderType: OrderPeriodOrderTypeIndicator;
  priority: number;
}

export interface OrderCategory {
  id: number;
  name: string;
  code: string;
  groupName: string;
  groupCode: string;
  priority: number;
}

export interface GamedaySettings {
  enabled: boolean;
  allowExport: boolean;
  showAccountNames: boolean;
}

export interface Rating {
  agency: string;
  rating: string;
  shortTerm: string;
  status?: RatingStatus;
  outlook?: RatingOutlook;
  freeText?: string;
  effectiveDate?: Moment;
  agencyCode?: null | string;
}

export interface CallFeature {
  securityType: SecurityType;
  displayText: string;
  type: CallType;
  freeText?: string;
  firstCallDate?: Moment;
  firstCallPrice?: number;
  lastCallDate?: Moment;
  lastCallPrice?: number;
  maxPrice?: number;
  percentIncrease?: number; // percentInc
  callFeatureSchedules?: CallFeatureSchedule[];
}

export interface CallFeatureSchedule {
  callDate: Moment;
  callPrice: number;
}

export interface Purpose {
  enabled: boolean;
  amount: number;
}

export enum CouponType {
  All = 'all',
  Single = 'single',
  Range = 'range',
  Zero = 'zero'
}

export interface CouponOption {
  type: CouponType;
  from: number;
  to: number;
  stringModel: string;
}

export interface SinkingFund {
  maturityDate: Moment;
  numberBonds: number;
  amount?: number;
  coupon?: number;
  priceOrBasis?: number;
  computedPrice?: number;
  concession?: number;
  td?: number;
}

export interface Documents {
  id: string;
  docName: string;
  docType: string;
}

export enum ESGCertType {
  Climate = 'Climate',
  Green = 'Green',
  Social = 'Social',
  Sustainable = 'Sustainable',
  Sustainability = 'Sustainability',
  Blue = 'Blue',
  Transition = 'Transition',
  Various = 'Various',
  Partial = 'Partial',
  Blank = 'Blank'
}

export enum ESGCertVerifier {
  Kestrel = 'Kestrel',
  EY = 'EY',
  BAMGreenStar = 'BAMGreenStar', //instead of 'BamGreenStar' in data
  FirstEnvironment = 'FirstEnvironment',
  Sustainalytics = 'Sustainalytics',

  Other = 'Other',
  Self = 'Self',

  Various = 'Various',
  Partial = 'Partial',
  Blank = 'Blank'
}

export interface ESGCertification {
  esgCertType: ESGCertType;
  esgCertVerifier: ESGCertVerifier;
  esgPreapprovedInd: boolean;
}

export interface Metrics {
  internalRating: string;
  internalScore: string;
  internalStatus: string;
  ownership: boolean;
}

export enum TreasuryFeed {
  oneYear = '1y',
  twoYears = '2y',
  threeYears = '3y',
  fiveYears = '5y',
  sevenYears = '7y',
  tenYears = '10y',
  thirtyYears = '30y'
}

export interface PacBondDesignation {
  pacInd: boolean;
  pacType: string;
  numYears: number;
  lowerLimit: number;
  upperLimit: number;
}

export interface SelectedDeals {
  [name: string]: string[];
}

export enum DealSource {
  grid = 'grid'
}
